import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Loader } from "@/components/common/Loader";
import api from "@/lib/api";
import { CONTENT_SCHEMAS, CONTENT_ORDER, canEditType, canManageSupport, ROLE_LABEL } from "@/config/contentSchemas";
import { useAuth } from "@/context/AuthContext";
import { LifeBuoy, Inbox, ArrowRight } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const role = user?.role;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const editableTypes = CONTENT_ORDER.filter((ct) => canEditType(role, ct));

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="display-title text-4xl">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Signed in as {ROLE_LABEL[role] || role}.</p>
        </div>
      </div>
      {loading || !data ? <Loader /> : (
        <>
          {canManageSupport(role) && (
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Link to="/admin/leads" className="rounded-xl border border-border bg-card p-5 transition-colors hover:border-brand">
                <div className="flex items-center justify-between"><Inbox className="h-6 w-6 text-brand" /><span className="display-title text-3xl">{data.new_leads ?? 0}</span></div>
                <p className="mt-2 text-sm text-muted-foreground">New leads / inquiries</p>
              </Link>
              <Link to="/admin/tickets" className="rounded-xl border border-border bg-card p-5 transition-colors hover:border-brand">
                <div className="flex items-center justify-between"><LifeBuoy className="h-6 w-6 text-brand" /><span className="display-title text-3xl">{data.open_tickets}</span></div>
                <p className="mt-2 text-sm text-muted-foreground">Open support tickets</p>
              </Link>
            </div>
          )}

          {editableTypes.length > 0 && (
            <>
              <h2 className="mt-10 display-title text-2xl">Content</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {editableTypes.map((ct) => {
                  const c = data.content[ct] || { total: 0 };
                  const schema = CONTENT_SCHEMAS[ct];
                  return (
                    <Link key={ct} to={`/admin/${ct}`} className="group rounded-xl border border-border bg-card p-5 transition-colors hover:border-brand">
                      <div className="flex items-center justify-between">
                        <span className="font-condensed text-sm font-semibold uppercase tracking-wide">{schema.label}</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                      </div>
                      <div className="mt-3 display-title text-3xl">{c.total}</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {c.Active > 0 && <StatusBadge status="Active" />}
                        {c.Draft > 0 && <StatusBadge status="Draft" />}
                        {c.Scheduled > 0 && <StatusBadge status="Scheduled" />}
                        {c.Expired > 0 && <StatusBadge status="Expired" />}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}

          {editableTypes.length === 0 && !canManageSupport(role) && (
            <p className="mt-8 text-muted-foreground">You don't have content permissions. Contact an administrator.</p>
          )}
        </>
      )}
    </AdminLayout>
  );
}
